import { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  ComposedChart,
  Line,
  Cell,
  CartesianGrid,
} from "recharts";
import {
  ChartPie,
  TrendUp,
  Storefront,
  SquaresFour,
  ChartBar,
} from "@phosphor-icons/react";
import api from "../../lib/api";
import { useTheme } from "../../contexts/ThemeContext";
import { fmtMoney } from "../../lib/finance-format";
import DateRangeChips from "./DateRangeChips";
import { useCategories } from "../../lib/useCategories";
import { CategoryIcon } from "./CategoryManagerDialog";

const CATEGORY_ES = {
  income: "Ingreso",
  housing: "Vivienda",
  food: "Comida",
  transport: "Transporte",
  subscription: "Suscripción",
  utilities: "Servicios",
  entertainment: "Entretenimiento",
  health: "Salud",
  education: "Educación",
  shopping: "Compras",
  travel: "Viajes",
  insurance: "Seguro",
  fees: "Comisiones",
  transfer: "Transferencia",
  other: "Otro",
};

const CATEGORY_ACCENT = {
  income: "#10B981",
  housing: "#2563EB",
  food: "#F97316",
  transport: "#6366F1",
  subscription: "#8B5CF6",
  utilities: "#0EA5E9",
  entertainment: "#EC4899",
  health: "#EF4444",
  education: "#14B8A6",
  shopping: "#F59E0B",
  travel: "#06B6D4",
  insurance: "#64748B",
  fees: "#DC2626",
  transfer: "#94A3B8",
  other: "#94A3B8",
};

const MONTH_ABBR_ES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const fmtMonth = (ym) => {
  if (!ym || ym.length < 7) return ym;
  const y = ym.slice(2, 4);
  const m = parseInt(ym.slice(5, 7), 10);
  return `${MONTH_ABBR_ES[m - 1]} '${y}`;
};

function CustomTooltip({ active, payload, label, mode, isDark, labelFor }) {
  if (!active || !payload || payload.length === 0) return null;
  const bg = isDark ? "#12131C" : "#FFFFFF";
  const border = isDark ? "rgba(255,255,255,0.14)" : "rgba(15,23,42,0.16)";
  const catLabel = labelFor ? labelFor(label) : (CATEGORY_ES[label] || label);
  return (
    <div
      style={{ background: bg, border: `1px solid ${border}` }}
      className="rounded-lg px-3 py-2 shadow-2xl text-[11px] font-mono"
    >
      <div className="font-heading font-bold text-white mb-1">
        {mode === "category" ? catLabel : mode === "month" ? fmtMonth(label) : label}
      </div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="text-white/60">{p.name}:</span>
          <span className="font-bold text-white tabular-nums">{fmtMoney(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * QuickBooks-style reports view: 3 cards
 *   1. Spending by category (bar) — last 12 months
 *   2. Cash flow history (income vs expenses vs net) — last 12 months
 *   3. Top merchants (horizontal bar)
 */
export default function ReportsPanel({ scope, onCategoryClick }) {
  const { isDark } = useTheme();
  const { byKey } = useCategories();
  const [spend, setSpend] = useState({ total: 0, items: [] });
  const [cashflow, setCashflow] = useState({ items: [] });
  const [merchants, setMerchants] = useState({ items: [] });
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState({ preset: "all", from: null, to: null });
  // Spending-by-category view: 'grid' (tile cards) | 'chart' (bar chart)
  const [spendView, setSpendView] = useState(() =>
    window.localStorage.getItem("cfo_spend_view") || "grid",
  );
  const setSpendViewPersisted = (v) => {
    setSpendView(v);
    window.localStorage.setItem("cfo_spend_view", v);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const params = new URLSearchParams();
        params.set("scope", scope || "all");
        if (range.from) params.set("from", range.from);
        if (range.to) params.set("to", range.to);
        const merchantsParams = new URLSearchParams(params);
        merchantsParams.set("limit", "10");
        const cashflowParams = new URLSearchParams(params);
        // If a range is chosen, request enough months to cover it; else default 12
        cashflowParams.set("months", range.preset === "last12" || range.preset === "all" ? "12" : "24");
        const [a, b, c] = await Promise.all([
          api.get(`/finance/reports/spending-by-category?${params.toString()}`),
          api.get(`/finance/reports/cashflow-history?${cashflowParams.toString()}`),
          api.get(`/finance/reports/top-merchants?${merchantsParams.toString()}`),
        ]);
        if (cancelled) return;
        setSpend(a.data);
        setCashflow(b.data);
        setMerchants(c.data);
      } catch (err) {
        /* silent */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [scope, range]);

  const gridColor = isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)";
  const axisColor = isDark ? "rgba(255,255,255,0.35)" : "rgba(15,23,42,0.45)";

  const labelFor = (key) => byKey[key]?.label || CATEGORY_ES[key] || key;
  const colorFor = (key) => byKey[key]?.color || CATEGORY_ACCENT[key] || "#94A3B8";

  const noData = spend.items.length === 0 && cashflow.items.length === 0 && merchants.items.length === 0;

  return (
    <div className="space-y-4" data-testid="reports-panel">
      {/* Date range filter */}
      <div className="rounded-lg border border-white/8 bg-black/20 p-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/40">
          RANGO DE ANÁLISIS
        </div>
        <DateRangeChips value={range} onChange={setRange} testId="reports-range" />
      </div>

      {noData && !loading ? (
        <div className="rounded-lg border border-white/8 bg-black/20 p-8 text-center" data-testid="reports-empty-state">
          <div className="w-14 h-14 rounded-2xl bg-[#FACC15]/15 border border-[#FACC15]/25 flex items-center justify-center mx-auto mb-3">
            <ChartPie size={22} weight="fill" className="text-[#FACC15]" />
          </div>
          <div className="text-lg font-heading font-bold text-white mb-1">
            Aún no hay transacciones
          </div>
          <div className="text-[12px] font-mono text-white/50 max-w-md mx-auto">
            Sube un PDF/CSV en la pestaña <b>Ingresos & Gastos</b> y las gráficas aparecerán aquí.
          </div>
        </div>
      ) : (
        <>
      {/* Spending by category */}
      <section className="rounded-lg border border-white/8 bg-black/20 p-4 md:p-5" data-testid="report-spending-category">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#FACC15]">
              SPENDING BY CATEGORY · TOTAL
            </div>
            <div className="flex items-baseline gap-2 mt-0.5">
              <h3 className="text-base md:text-lg font-heading font-bold text-white">
                Gasto por categoría
              </h3>
              <span className="text-lg font-mono font-bold text-white tabular-nums">
                {fmtMoney(spend.total)}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Grid / Chart view toggle */}
            <div
              data-testid="spend-view-toggle"
              className="inline-flex items-center p-0.5 rounded-lg bg-black/40 border border-white/8"
            >
              <button
                data-testid="spend-view-grid"
                onClick={() => setSpendViewPersisted("grid")}
                title="Vista de cuadrícula"
                className={`w-8 h-8 rounded-md flex items-center justify-center transition ${
                  spendView === "grid"
                    ? "bg-[#FACC15] text-slate-900 ring-2 ring-[#FACC15]/40"
                    : "text-white/50 hover:text-white hover:bg-white/6"
                }`}
              >
                <SquaresFour size={14} weight="fill" />
              </button>
              <button
                data-testid="spend-view-chart"
                onClick={() => setSpendViewPersisted("chart")}
                title="Vista de gráfico"
                className={`w-8 h-8 rounded-md flex items-center justify-center transition ${
                  spendView === "chart"
                    ? "bg-[#FACC15] text-slate-900 ring-2 ring-[#FACC15]/40"
                    : "text-white/50 hover:text-white hover:bg-white/6"
                }`}
              >
                <ChartBar size={14} weight="fill" />
              </button>
            </div>
            <ChartPie size={18} weight="fill" className="text-[#FACC15]/60" />
          </div>
        </div>
        {spend.items.length > 0 ? spendView === "grid" ? (
          <SpendingCategoryGrid
            items={spend.items}
            total={spend.total}
            labelFor={labelFor}
            iconFor={(k) => byKey[k]?.icon || "Tag"}
            colorFor={colorFor}
            onCategoryClick={onCategoryClick}
          />
        ) : (
          <>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={spend.items} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                <XAxis
                  dataKey="category"
                  tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: axisColor }}
                  tickFormatter={(v) => (labelFor(v)).slice(0, 8)}
                  tickLine={false}
                  axisLine={{ stroke: gridColor }}
                />
                <YAxis
                  tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: axisColor }}
                  tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v)}
                  tickLine={false}
                  axisLine={{ stroke: gridColor }}
                />
                <Tooltip content={<CustomTooltip mode="category" isDark={isDark} labelFor={labelFor} />} cursor={{ fill: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)" }} />
                <Bar dataKey="total" name="Gastado" radius={[4, 4, 0, 0]}>
                  {spend.items.map((entry) => (
                    <Cell key={entry.category} fill={colorFor(entry.category)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            {/* Legend as small table */}
            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
              {spend.items.slice(0, 6).map((it) => (
                <div key={it.category} className="flex items-center gap-2 text-[11px] font-mono">
                  <span className="w-2 h-2 rounded-full" style={{ background: colorFor(it.category) }} />
                  <span className="text-white/70">{labelFor(it.category)}</span>
                  <span className="ml-auto text-white/50 tabular-nums">
                    {fmtMoney(it.total)}  ·  {it.percent}%
                  </span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <EmptyGraph text="No hay gastos categorizados aún." />
        )}
      </section>

      {/* Cash flow history */}
      <section className="rounded-lg border border-white/8 bg-black/20 p-4 md:p-5" data-testid="report-cashflow">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-400">
              CASH FLOW · {range.preset === "all" ? "TODO" : range.preset === "last12" ? "ÚLTIMOS 12 MESES" : range.preset.startsWith("y") ? range.preset.slice(1) : "RANGO"}
            </div>
            <h3 className="text-base md:text-lg font-heading font-bold text-white">
              Ingresos vs Gastos
            </h3>
          </div>
          <TrendUp size={18} weight="fill" className="text-emerald-400/60" />
        </div>
        {cashflow.items.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={cashflow.items} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: axisColor }}
                tickFormatter={fmtMonth}
                tickLine={false}
                axisLine={{ stroke: gridColor }}
              />
              <YAxis
                tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: axisColor }}
                tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v)}
                tickLine={false}
                axisLine={{ stroke: gridColor }}
              />
              <Tooltip content={<CustomTooltip mode="month" isDark={isDark} />} cursor={{ fill: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)" }} />
              <Bar dataKey="income" name="Ingresos" fill="#10B981" radius={[3, 3, 0, 0]} />
              <Bar dataKey="expenses" name="Gastos" fill="#EF4444" radius={[3, 3, 0, 0]} />
              <Line type="monotone" dataKey="net" name="Neto" stroke="#FACC15" strokeWidth={2.5} dot={{ fill: "#FACC15", r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <EmptyGraph text="Aún no hay historial mensual." />
        )}
      </section>

      {/* Top merchants */}
      <section className="rounded-lg border border-white/8 bg-black/20 p-4 md:p-5" data-testid="report-top-merchants">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#2563EB]">
              TOP 10 · MERCHANTS
            </div>
            <h3 className="text-base md:text-lg font-heading font-bold text-white">
              Donde más gastas
            </h3>
          </div>
          <Storefront size={18} weight="fill" className="text-[#2563EB]/60" />
        </div>
        {merchants.items.length > 0 ? (
          <div className="space-y-2">
            {merchants.items.map((m, i) => {
              const max = merchants.items[0]?.total || 1;
              const pct = (m.total / max) * 100;
              return (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`merchant-row-${i}`}>
                  <div className="col-span-1 text-[10px] font-mono text-white/40 tabular-nums text-right">
                    #{i + 1}
                  </div>
                  <div className="col-span-5 text-sm font-heading font-semibold text-white truncate">
                    {m.merchant}
                  </div>
                  <div className="col-span-4">
                    <div className="h-2 rounded-full bg-white/6 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${pct}%`, background: "linear-gradient(90deg, #2563EB, #6366F1)" }}
                      />
                    </div>
                  </div>
                  <div className="col-span-2 text-right">
                    <div className="text-sm font-mono font-bold text-white tabular-nums">
                      {fmtMoney(m.total)}
                    </div>
                    <div className="text-[9px] font-mono text-white/40">
                      {m.count} pagos
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyGraph text="Aún no hay comercios registrados." />
        )}
      </section>
        </>
      )}
    </div>
  );
}

function EmptyGraph({ text }) {
  return (
    <div className="h-40 flex items-center justify-center text-xs font-mono text-white/30">
      {text}
    </div>
  );
}

// ------------------------------------------------------------------
// LaunchPad-style tile grid for "Spending by category".
// Sorted by amount desc; each tile shows icon/color, label, amount, %.
// ------------------------------------------------------------------
function SpendingCategoryGrid({ items, total, labelFor, iconFor, colorFor, onCategoryClick }) {
  const sorted = [...items].sort((a, b) => b.total - a.total);
  return (
    <div
      data-testid="spending-category-grid"
      className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3"
    >
      {sorted.map((it) => {
        const color = colorFor(it.category);
        const label = labelFor(it.category);
        const icon = iconFor(it.category);
        const pct = it.percent != null
          ? it.percent
          : total > 0
            ? Math.round((it.total / total) * 1000) / 10
            : 0;
        const clickable = !!onCategoryClick;
        const Tag = clickable ? "button" : "div";
        return (
          <Tag
            key={it.category}
            data-testid={`spending-tile-${it.category}`}
            onClick={clickable ? () => onCategoryClick(it.category) : undefined}
            title={clickable ? `Ver transacciones de ${label}` : undefined}
            className={`relative overflow-hidden rounded-2xl p-3 text-left w-full transition ${
              clickable
                ? "cursor-pointer hover:scale-[1.03] hover:-translate-y-0.5 active:scale-[0.98]"
                : "hover:scale-[1.02] hover:-translate-y-0.5"
            }`}
            style={{
              background: `linear-gradient(135deg, ${color}22 0%, ${color}0F 100%)`,
              border: `1px solid ${color}55`,
            }}
          >
            {/* Percent chip */}
            <div
              className="absolute top-2 right-2 on-solid text-[9px] font-mono font-bold px-1.5 py-0.5 rounded"
              style={{ backgroundColor: color, color: "#FFF" }}
            >
              {pct}%
            </div>
            {/* Icon plate */}
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center mb-2 shadow-lg"
              style={{
                backgroundColor: color,
                boxShadow: `0 6px 20px -8px ${color}80`,
              }}
            >
              <CategoryIcon
                name={icon}
                size={20}
                weight="fill"
                style={{ color: "#FFF" }}
              />
            </div>
            {/* Label */}
            <div className="text-[12px] font-heading font-bold text-white truncate">
              {label}
            </div>
            {/* Amount */}
            <div className="text-[13px] font-mono font-bold text-white tabular-nums mt-0.5">
              {fmtMoney(it.total)}
            </div>
            {/* Optional count */}
            {it.count != null && (
              <div className="text-[9px] font-mono text-white/40 mt-0.5">
                {it.count} mov.
              </div>
            )}
          </Tag>
        );
      })}
    </div>
  );
}
